import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import ProcessSelector from './ProcessSelector';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Video, 
  Square, 
  Pause, 
  Play,
  Save,
  Loader2,
  Monitor,
  Mic,
  MicOff,
  Clock,
  MousePointer,
  AlertCircle,
  CheckCircle,
  Camera,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

const ScreenRecorder = ({ onComplete, defaultLinkedProcessId }) => {
  const navigate = useNavigate();
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);
  const [includeAudio, setIncludeAudio] = useState(false);
  const [autoCapture, setAutoCapture] = useState(true);
  const [l2Processes, setL2Processes] = useState([]);
  const [l3Processes, setL3Processes] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [steps, setSteps] = useState([]);
  const [capturingStep, setCapturingStep] = useState(false);
  const [linkedProcessId, setLinkedProcessId] = useState(defaultLinkedProcessId || null);
  const [linkedProcessLevel, setLinkedProcessLevel] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    department: '',
    process_area: ''
  });

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const previewVideoRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);
  const canvasRef = useRef(null);
  const autoCaptureTimerRef = useRef(null);
  const compCanvasRef = useRef(null);
  const prevFrameRef = useRef(null);
  const stepCountRef = useRef(0);

  useEffect(() => {
    fetchDepartments();
    canvasRef.current = document.createElement('canvas');
    // Small canvas for fast frame comparison
    const compCanvas = document.createElement('canvas');
    compCanvas.width = 160;
    compCanvas.height = 90;
    compCanvasRef.current = compCanvas;
    return () => {
      stopRecording();
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoCaptureTimerRef.current) clearInterval(autoCaptureTimerRef.current);
    };
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/processes');
      setL2Processes(response.data.filter(p => p.level === 2));
      setL3Processes(response.data.filter(p => p.level === 3));
    } catch (error) {
      console.error('Failed to fetch processes:', error);
    }
  };

  const startRecording = async () => {
    try {
      // Request screen capture
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor'
        },
        audio: includeAudio
      });

      // If audio requested, try to get microphone
      let combinedStream = displayStream;
      if (includeAudio) {
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const tracks = [...displayStream.getTracks(), ...audioStream.getTracks()];
          combinedStream = new MediaStream(tracks);
        } catch (audioErr) {
          console.warn('Could not get microphone:', audioErr);
          toast.warning('Recording without microphone');
        }
      }

      streamRef.current = combinedStream;
      chunksRef.current = [];

      // Create a hidden video element for reliable screenshot capture
      const previewVideo = document.createElement('video');
      previewVideo.srcObject = new MediaStream([displayStream.getVideoTracks()[0]]);
      previewVideo.muted = true;
      previewVideo.playsInline = true;
      previewVideo.style.position = 'absolute';
      previewVideo.style.top = '-9999px';
      document.body.appendChild(previewVideo);
      await previewVideo.play();
      previewVideoRef.current = previewVideo;

      // Set up MediaRecorder
      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: 'video/webm;codecs=vp9'
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Recording stopped
      };

      // Handle stream ending (user clicked "Stop sharing")
      displayStream.getVideoTracks()[0].onended = () => {
        stopRecording();
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Capture in 1-second chunks

      startTimeRef.current = Date.now();
      setRecording(true);
      setHasPermission(true);
      setSteps([]);
      stepCountRef.current = 0;
      prevFrameRef.current = null;

      // Start timer
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);

      // Start auto-capture if enabled
      if (autoCapture) {
        startAutoCapture();
      }

      toast.success(autoCapture 
        ? 'Recording started! Auto-capturing every 3s when screen changes.' 
        : 'Recording started! Click "Capture Step" to take screenshots.'
      );

    } catch (error) {
      console.error('Failed to start recording:', error);
      setHasPermission(false);
      if (error.name === 'NotAllowedError') {
        toast.error('Screen sharing permission denied');
      } else {
        toast.error('Failed to start screen recording');
      }
    }
  };

  // Capture screenshot from the hidden video element
  const captureScreenshot = async () => {
    const video = previewVideoRef.current;
    if (!video || video.readyState < 2) {
      console.warn('Video not ready for screenshot capture');
      return null;
    }

    try {
      const canvas = canvasRef.current;
      const maxWidth = 1280;
      const scale = video.videoWidth > maxWidth ? maxWidth / video.videoWidth : 1;
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.5);
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      return null;
    }
  };

  // Compare two frames using downsampled pixel data. Returns % of changed pixels.
  const getFrameDiff = (currentData, prevData) => {
    if (!prevData || currentData.length !== prevData.length) return 100;
    const threshold = 30; // RGB channel diff threshold per pixel
    let changed = 0;
    const totalPixels = currentData.length / 4;
    // Sample every 4th pixel for speed
    for (let i = 0; i < currentData.length; i += 16) {
      const dr = Math.abs(currentData[i] - prevData[i]);
      const dg = Math.abs(currentData[i + 1] - prevData[i + 1]);
      const db = Math.abs(currentData[i + 2] - prevData[i + 2]);
      if (dr > threshold || dg > threshold || db > threshold) changed++;
    }
    return (changed / (totalPixels / 4)) * 100;
  };

  // Auto-capture: grab a frame, compare with previous, save if changed
  const autoCaptureTickRef = useRef(null);
  autoCaptureTickRef.current = async () => {
    const video = previewVideoRef.current;
    if (!video || video.readyState < 2) return;

    try {
      const comp = compCanvasRef.current;
      const ctx = comp.getContext('2d');
      ctx.drawImage(video, 0, 0, comp.width, comp.height);
      const currentData = ctx.getImageData(0, 0, comp.width, comp.height).data;

      const diffPct = getFrameDiff(currentData, prevFrameRef.current);

      if (diffPct > 3) {
        // Screen changed — capture full-res screenshot and add step
        const screenshotUrl = await captureScreenshot();
        const timestamp = (Date.now() - startTimeRef.current) / 1000;
        stepCountRef.current += 1;
        const newStep = {
          id: `auto-${Date.now()}`,
          timestamp,
          action_type: 'auto',
          description: '',
          screenshot_url: screenshotUrl,
          element_text: `Auto Step ${stepCountRef.current}`
        };
        setSteps(prev => [...prev, newStep]);
      }

      prevFrameRef.current = new Uint8ClampedArray(currentData);
    } catch (err) {
      console.error('Auto-capture tick error:', err);
    }
  };

  const startAutoCapture = () => {
    if (autoCaptureTimerRef.current) clearInterval(autoCaptureTimerRef.current);
    autoCaptureTimerRef.current = setInterval(() => {
      autoCaptureTickRef.current?.();
    }, 3000);
  };

  const stopAutoCapture = () => {
    if (autoCaptureTimerRef.current) {
      clearInterval(autoCaptureTimerRef.current);
      autoCaptureTimerRef.current = null;
    }
  };

  const addManualStep = async () => {
    if (capturingStep) return;
    
    setCapturingStep(true);
    const timestamp = (Date.now() - startTimeRef.current) / 1000;
    
    // Capture screenshot
    const screenshotUrl = await captureScreenshot();
    stepCountRef.current += 1;
    
    const newStep = {
      id: `step-${Date.now()}`,
      timestamp,
      action_type: 'manual',
      description: '',
      screenshot_url: screenshotUrl,
      element_text: `Step ${stepCountRef.current}`
    };
    
    setSteps(prev => [...prev, newStep]);
    setCapturingStep(false);
    
    if (screenshotUrl) {
      toast.success(`Step captured with screenshot at ${formatTime(timestamp)}`);
    } else {
      toast.info(`Step marked at ${formatTime(timestamp)} (no screenshot)`);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && recording) {
      if (paused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 1000);
        if (autoCapture) startAutoCapture();
        setPaused(false);
        toast.info('Recording resumed');
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) clearInterval(timerRef.current);
        stopAutoCapture();
        setPaused(true);
        toast.info('Recording paused');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    // Clean up preview video element
    if (previewVideoRef.current) {
      previewVideoRef.current.pause();
      previewVideoRef.current.srcObject = null;
      previewVideoRef.current.remove();
      previewVideoRef.current = null;
    }
    stopAutoCapture();
    prevFrameRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setRecording(false);
    setPaused(false);
  };

  const saveCapture = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name for the capture');
      return;
    }

    setSaving(true);
    try {
      // Separate screenshots from steps to avoid large payload
      const screenshotMap = {};
      const stepsWithoutScreenshots = steps.map(s => {
        if (s.screenshot_url) {
          screenshotMap[s.id] = s.screenshot_url;
        }
        return { ...s, screenshot_url: null };
      });

      // 1. Create the capture record (small payload)
      const captureData = {
        name: formData.name,
        description: formData.description,
        department: formData.department,
        steps: stepsWithoutScreenshots,
        duration: elapsedTime,
        linked_process_id: linkedProcessId,
        linked_process_level: linkedProcessLevel,
      };

      const response = await api.post('/process-captures', captureData);
      const captureId = response.data.id;

      // 2. Upload screenshots individually
      const screenshotEntries = Object.entries(screenshotMap);
      if (screenshotEntries.length > 0) {
        toast.info(`Uploading ${screenshotEntries.length} screenshots...`);
        for (const [stepId, dataUrl] of screenshotEntries) {
          try {
            // Convert base64 data URL to Blob
            const res = await fetch(dataUrl);
            const blob = await res.blob();
            const formPayload = new FormData();
            formPayload.append('file', blob, `${stepId}.jpg`);

            await api.post(
              `/process-captures/${captureId}/upload-screenshot?step_id=${stepId}`,
              formPayload,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
          } catch (uploadErr) {
            console.error(`Failed to upload screenshot for ${stepId}:`, uploadErr);
          }
        }
      }

      toast.success('Process capture saved!');
      
      if (onComplete) {
        onComplete(captureId);
      } else {
        navigate(`/process-capture/${captureId}`);
      }
    } catch (error) {
      console.error('Failed to save capture:', error);
      toast.error('Failed to save capture');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6" data-testid="screen-recorder">
      {/* Recording Status Card */}
      <Card className={recording ? 'border-2 border-red-500' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Screen Recorder
            {recording && (
              <Badge variant="destructive" className="animate-pulse">
                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                Recording
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pre-recording Setup */}
          {!recording && elapsedTime === 0 && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  How it works
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>- Click "Start Recording" and choose your screen, window, or tab</li>
                  <li>- Perform your process steps (works with any application)</li>
                  <li>- <strong>Auto-capture</strong> takes a screenshot every 3s when the screen changes</li>
                  <li>- You can also click "Capture Step" manually for important moments</li>
                  <li>- Stop recording when finished, then use AI to enhance descriptions</li>
                </ul>
              </div>

              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoCapture}
                    onChange={(e) => setAutoCapture(e.target.checked)}
                    className="rounded"
                    data-testid="auto-capture-toggle"
                  />
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span className="text-sm">Auto-capture every 3s (only when screen changes)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAudio}
                    onChange={(e) => setIncludeAudio(e.target.checked)}
                    className="rounded"
                  />
                  {includeAudio ? <Mic className="w-4 h-4 text-emerald-500" /> : <MicOff className="w-4 h-4 text-slate-400" />}
                  <span className="text-sm">Include microphone audio</span>
                </label>
              </div>

              <Button 
                onClick={startRecording}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                size="lg"
                data-testid="start-recording-btn"
              >
                <Video className="w-5 h-5 mr-2" />
                Start Recording
              </Button>

              {hasPermission === false && (
                <p className="text-sm text-red-600 text-center">
                  Screen sharing was denied. Please allow screen capture to continue.
                </p>
              )}
            </div>
          )}

          {/* Recording Controls */}
          {recording && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-6 py-4">
                <div className="text-center">
                  <div className="text-4xl font-mono font-bold text-slate-900">
                    {formatTime(elapsedTime)}
                  </div>
                  <p className="text-sm text-slate-500">Duration</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-slate-900">
                    {steps.length}
                  </div>
                  <p className="text-sm text-slate-500">Steps</p>
                </div>
              </div>

              {autoCapture && (
                <div className="flex items-center justify-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-lg py-2 px-3">
                  <Zap className="w-4 h-4" />
                  Auto-capturing every 3s when screen changes
                </div>
              )}

              <div className="flex gap-2 justify-center">
                <Button 
                  variant="outline"
                  onClick={pauseRecording}
                >
                  {paused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
                  {paused ? 'Resume' : 'Pause'}
                </Button>
                <Button 
                  onClick={addManualStep}
                  disabled={capturingStep}
                  className="bg-[#235894] hover:bg-[#1E4B80]"
                  data-testid="mark-step-btn"
                >
                  {capturingStep ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 mr-2" />
                  )}
                  Capture Step
                </Button>
                <Button 
                  variant="destructive"
                  onClick={stopRecording}
                  data-testid="stop-recording-btn"
                >
                  <Square className="w-4 h-4 mr-2" />
                  Stop Recording
                </Button>
              </div>

              {steps.length > 0 && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg max-h-60 overflow-y-auto">
                  <p className="text-xs text-slate-500 mb-2">Captured Steps:</p>
                  <div className="space-y-2">
                    {steps.map((step, i) => (
                      <div key={step.id} className="flex items-center gap-3 p-2 bg-white rounded border">
                        {step.screenshot_url ? (
                          <img 
                            src={step.screenshot_url} 
                            alt={`Step ${i + 1}`}
                            className="w-20 h-12 object-cover rounded border"
                          />
                        ) : (
                          <div className="w-20 h-12 bg-slate-200 rounded flex items-center justify-center text-xs text-slate-400">
                            No image
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-xs">
                              {formatTime(step.timestamp)}
                            </Badge>
                            {step.action_type === 'auto' ? (
                              <Badge className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-100">
                                <Zap className="w-3 h-3 mr-0.5" /> Auto
                              </Badge>
                            ) : (
                              <Badge className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-100">
                                <Camera className="w-3 h-3 mr-0.5" /> Manual
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium mt-1">Step {i + 1}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Post-recording: Save Form */}
          {!recording && elapsedTime > 0 && (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Recording Complete!</span>
                </div>
                <p className="text-sm text-emerald-700 mt-1">
                  Duration: {formatTime(elapsedTime)} • {steps.length} steps marked
                </p>
              </div>

              <div>
                <Label htmlFor="name">Capture Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Invoice Approval Process"
                  data-testid="capture-name-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this process..."
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="department">Department (Value Stream)</Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, department: value, process_area: '' }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {l2Processes.map((p) => (
                      <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="process_area">Process Area (E2E Process)</Label>
                <Select
                  value={formData.process_area || ''}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, process_area: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={formData.department ? "Select process area" : "Select department first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const selectedL2 = l2Processes.find(p => p.name === formData.department);
                      const filtered = selectedL2 ? l3Processes.filter(p => p.parent_process_id === selectedL2.id) : l3Processes;
                      return filtered.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>);
                    })()}
                  </SelectContent>
                </Select>
              </div>

              <ProcessSelector
                value={linkedProcessId}
                onChange={(procId, level) => { setLinkedProcessId(procId); setLinkedProcessLevel(level); }}
                label="Linked Process"
              />

              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setElapsedTime(0);
                    setSteps([]);
                  }}
                  className="flex-1"
                >
                  Discard & Restart
                </Button>
                <Button 
                  onClick={saveCapture}
                  disabled={saving || !formData.name.trim()}
                  className="flex-1 bg-[#235894] hover:bg-[#1E4B80]"
                  data-testid="save-capture-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Capture
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ScreenRecorder;
